import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Grid, Card, CardContent, Typography, Button, Box, Chip, Avatar, LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Alert
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { projectService, invoiceService, proposalService, scopeService, contractService } from '../services/api';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import PaymentIcon from '@mui/icons-material/Payment';
import PendingIcon from '@mui/icons-material/Pending';
import DescriptionIcon from '@mui/icons-material/Description';
import HomeIcon from '@mui/icons-material/Home';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import AddIcon from '@mui/icons-material/Add';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FolderIcon from '@mui/icons-material/Folder';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const StatCard = ({ title, value, icon, color, subtitle }) => (
  <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3, height: '100%' }}>
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ bgcolor: `${color}15`, borderRadius: 2, p: 1.5 }}>
          {React.cloneElement(icon, { sx: { color, fontSize: 28 } })}
        </Box>
        <TrendingUpIcon sx={{ color: '#4caf50', fontSize: 20 }} />
      </Box>
      <Typography variant="h3" sx={{ fontWeight: 700, color: '#1a237e', mb: 0.5 }}>{value}</Typography>
      <Typography variant="body2" sx={{ color: '#666', fontWeight: 600 }}>{title}</Typography>
      {subtitle && <Typography variant="caption" sx={{ color: '#999' }}>{subtitle}</Typography>}
    </CardContent>
  </Card>
);

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [openScopes, setOpenScopes] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [reminderDialog, setReminderDialog] = useState(false);
  const [reminderForm, setReminderForm] = useState({ title: '', note: '', priority: 'medium', due_date: '' });
  const [reminderSaving, setReminderSaving] = useState(false);
  const [subSummary, setSubSummary] = useState(null);
  const [bdSort, setBdSort] = useState('invoiced');
  const [ownerSummary, setOwnerSummary] = useState(null);
  const [planState, setPlanState] = useState(null);
  const [todos, setTodos] = useState([]);
  const [newTodoText, setNewTodoText] = useState('');
  const [todoSaving, setTodoSaving] = useState(false);

  useEffect(() => {
    if (user?.role === 'homeowner') {
      projectService.getAll().then(res => setProjects(res.data)).catch(() => {});
      invoiceService.getAll().then(res => setInvoices(res.data)).catch(() => {});
      api.get('/reminders').then(r => setReminders(Array.isArray(r.data) ? r.data : [])).catch(() => {});
      api.get('/dashboard/owner-summary').then(r => setOwnerSummary(r.data)).catch(() => {});
    } else if (user?.role === 'general_contractor') {
      projectService.getAll().then(r => setProjects(r.data)).catch(() => {});
      invoiceService.getAll().then(r => setInvoices(r.data)).catch(() => {});
      api.get('/contracts').then(r => setContracts(Array.isArray(r.data) ? r.data : [])).catch(() => {});
      api.get('/reminders').then(r => setReminders(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    } else if (user?.role === 'subcontractor') {
      proposalService.getMy().then(res => setProposals(res.data)).catch(() => {});
      invoiceService.getAll().then(res => setInvoices(res.data)).catch(() => {});
      scopeService.getOpen().then(res => setOpenScopes(res.data)).catch(() => {});
      contractService.getAll().then(res => setContracts(res.data)).catch(() => {});
      api.get('/dashboard/sub-summary').then(r => setSubSummary(r.data)).catch(() => {});
      api.get('/subscription/me').then(r => setPlanState(r.data)).catch(() => {});
      api.get('/user-todos').then(r => setTodos(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    }
  }, [user]);

  const saveReminder = async () => {
    if (!reminderForm.title) return;
    setReminderSaving(true);
    try {
      const r = await api.post('/reminders', reminderForm);
      setReminders([r.data, ...reminders]);
      setReminderDialog(false);
      setReminderForm({ title: '', note: '', priority: 'medium', due_date: '' });
    } catch(e) {}
    setReminderSaving(false);
  };

  const addTodo = async () => {
    const text = newTodoText.trim();
    if (!text) return;
    setTodoSaving(true);
    try {
      const r = await api.post('/user-todos', { text });
      setTodos([r.data, ...todos]);
      setNewTodoText('');
    } catch(e) {}
    setTodoSaving(false);
  };

  const toggleTodo = async (todo) => {
    try {
      const r = await api.patch(`/user-todos/${todo.id}`, { done: !todo.done });
      setTodos(todos.map(t => t.id === todo.id ? r.data : t));
    } catch(e) {}
  };

  const deleteTodo = async (todo) => {
    try {
      await api.delete(`/user-todos/${todo.id}`);
      setTodos(todos.filter(t => t.id !== todo.id));
    } catch(e) {}
  };


  const completeReminder = async (id) => {
    await api.put('/reminders/' + id + '/status', { status: 'completed' });
    setReminders(reminders.map(r => r.id === id ? { ...r, status: 'completed' } : r));
  };

  const deleteReminder = async (id) => {
    await api.delete('/reminders/' + id);
    setReminders(reminders.filter(r => r.id !== id));
  };

  const priorityColor = (p) => ({
    high:   { bg: '#ffebee', color: '#c62828' },
    medium: { bg: '#fff3e0', color: '#e65100' },
    low:    { bg: '#e8f5e9', color: '#2e7d32' },
  }[p] || { bg: '#f5f5f5', color: '#555' });

  const ReminderWidget = () => (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a237e' }}>
          📋 Tasks & Reminders
        </Typography>
        <Button size="small" variant="contained" onClick={() => setReminderDialog(true)}
          sx={{ bgcolor: '#1a237e', borderRadius: 2, fontSize: 11 }}>+ Add Task</Button>
      </Box>
      {reminders.length === 0 ? (
        <Card elevation={0} sx={{ border: '1px dashed #c5cae9', borderRadius: 3, p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">No tasks yet. Add your first reminder!</Typography>
        </Card>
      ) : (
        <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f5f6fa' }}>
                {['Title','Priority','Due Date',''].map(h => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#666', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {reminders.map((r, idx) => {
                const pc = priorityColor(r.priority);
                const isOverdue = r.due_date && new Date(r.due_date) < new Date() && r.status !== 'completed';
                return (
                  <tr key={r.id} style={{ borderTop: '1px solid #f0f0f0', background: idx%2===0 ? 'white' : '#fafafa', opacity: r.status === 'completed' ? 0.6 : 1 }}>
                    <td style={{ padding: '8px 12px', fontSize: 13, fontWeight: 600, textDecoration: r.status === 'completed' ? 'line-through' : 'none' }}>{r.title}</td>
                    <td style={{ padding: '10px 16px' }}>
                      <Chip label={r.priority} size="small" sx={{ bgcolor: pc.bg, color: pc.color, fontWeight: 700, fontSize: 10 }}/>
                    </td>
                    <td style={{ padding: '10px 16px', fontSize: 12, color: isOverdue ? '#c62828' : '#555', fontWeight: isOverdue ? 700 : 400, whiteSpace: 'nowrap' }}>
                      {r.due_date ? (isOverdue ? '⚠️ ' : '📅 ') + new Date(r.due_date).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {r.status !== 'completed' && (
                          <Box onClick={() => completeReminder(r.id)}
                            sx={{ cursor: 'pointer', color: '#43a047', fontWeight: 700, fontSize: 13, '&:hover': { opacity: 0.7 } }}>✓ Done</Box>
                        )}
                        <Box onClick={() => deleteReminder(r.id)}
                          sx={{ cursor: 'pointer', color: '#e53935', fontWeight: 700, fontSize: 13, '&:hover': { opacity: 0.7 } }}>✕</Box>
                      </Box>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </Box>
  );

  return (
    <Layout>
      {user?.role !== 'subcontractor' && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a237e' }}>
            Welcome back, {user?.first_name}! 👋
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', mt: 0.5 }}>
            Here's what's happening with your projects today.
          </Typography>
        </Box>
      )}

      {user?.role === 'homeowner' && (
        <>
          {ownerSummary?.has_project && (
            <>
              {/* Your Project hero */}
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1a237e' }}>Your Project</Typography>
              {(() => {
                const proj = projects.find(p => p.id === ownerSummary.project.id) || projects[0] || {};
                const fmt = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '\u2014';
                let daysRemaining = null;
                if (proj.end_date) daysRemaining = Math.ceil((new Date(proj.end_date) - Date.now()) / 86400000);
                const st = ownerSummary.project.status;
                return (
                  <Box onClick={() => navigate('/projects/' + ownerSummary.project.id)}
                    sx={{ mb: 3, borderRadius: 3, border: '1px solid #e8eaf6', overflow: 'hidden', bgcolor: 'white', cursor: 'pointer',
                      display: 'flex', flexDirection: { xs: 'column', md: 'row' },
                      '&:hover': { boxShadow: '0 8px 24px rgba(26,35,126,0.12)' } }}>
                    <Box sx={{ width: { xs: '100%', md: 300 }, minHeight: 200, flexShrink: 0 }}>
                      {proj.image_url ? (
                        <img src={proj.image_url} alt={ownerSummary.project.name}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', minHeight: 200 }} />
                      ) : (
                        <Box sx={{ width: '100%', height: '100%', minHeight: 200, background: 'linear-gradient(135deg,#1a237e,#283593)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Typography variant="h2" sx={{ color: 'rgba(255,255,255,0.25)', fontWeight: 800 }}>
                            {ownerSummary.project.name?.[0]?.toUpperCase()}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ flex: 1, p: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#1a237e' }}>{ownerSummary.project.name}</Typography>
                        <Chip label={st} size="small" sx={{ textTransform: 'capitalize', fontWeight: 700,
                          bgcolor: st === 'active' ? '#e8f5e9' : '#fff3e0', color: st === 'active' ? '#2e7d32' : '#e65100' }} />
                      </Box>
                      <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>{ownerSummary.project.address}</Typography>
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>Start</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmt(proj.start_date)}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>Target end</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmt(proj.end_date)}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>Contracts</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{ownerSummary.contracts.active} active / {ownerSummary.contracts.total}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>Milestones</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{ownerSummary.milestones.done}/{ownerSummary.milestones.total}</Typography>
                        </Grid>
                      </Grid>
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" sx={{ color: '#666' }}>
                            {daysRemaining !== null ? (daysRemaining >= 0 ? daysRemaining + ' days remaining' : Math.abs(daysRemaining) + ' days past target') : 'Progress'}
                          </Typography>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: '#2e7d32' }}>{ownerSummary.financials.progress_pct}%</Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={ownerSummary.financials.progress_pct}
                          sx={{ height: 8, borderRadius: 4, bgcolor: '#e8eaf6', '& .MuiLinearProgress-bar': { bgcolor: '#2e7d32', borderRadius: 4 } }} />
                      </Box>
                    </Box>
                  </Box>
                );
              })()}
              {/* Financial health */}
              <Box sx={{ mb: 3, p: 3, borderRadius: 3, border: '1px solid #e8eaf6', bgcolor: 'white' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a237e', mb: 2 }}>Financial Summary</Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {[
                    { label: 'Budget', value: ownerSummary.financials.budget, color: '#5e35b1' },
                    { label: 'Contracted', value: ownerSummary.financials.contracted, color: '#1a237e' },
                    { label: 'Invoiced', value: ownerSummary.financials.invoiced, color: '#e65100' },
                    { label: 'Paid', value: ownerSummary.financials.paid, color: '#2e7d32' },
                    { label: 'Remaining', value: ownerSummary.financials.remaining, color: '#0277bd' },
                  ].map(m => (
                    <Grid item xs={6} md={2.4} key={m.label}>
                      <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f5f6fa', textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>{m.label}</Typography>
                        <Typography sx={{ fontWeight: 800, color: m.color, fontSize: 20 }}>
                          ${Number(m.value).toLocaleString()}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                {/* Progress */}
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      Project progress (paid / contracted)
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                      {ownerSummary.financials.progress_pct}%
                    </Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={ownerSummary.financials.progress_pct}
                    sx={{ height: 10, borderRadius: 5, bgcolor: '#e8eaf6', '& .MuiLinearProgress-bar': { bgcolor: '#2e7d32', borderRadius: 5 } }} />
                  {ownerSummary.milestones.total > 0 && (
                    <Typography variant="caption" sx={{ color: '#999', mt: 0.5, display: 'block' }}>
                      Milestones: {ownerSummary.milestones.done}/{ownerSummary.milestones.total} complete ({ownerSummary.milestones.pct}%)
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Needs attention */}
              {(() => {
                const na = ownerSummary.needs_attention;
                const items = [];
                if (na.invoices_to_approve > 0) items.push({ label: na.invoices_to_approve + ' invoice' + (na.invoices_to_approve > 1 ? 's' : '') + ' to approve', sub: '$' + na.invoices_to_approve_amount.toLocaleString(), color: '#e65100', bg: '#fff3e0', path: '/invoices' });
                if (na.contracts_to_sign > 0) items.push({ label: na.contracts_to_sign + ' contract' + (na.contracts_to_sign > 1 ? 's' : '') + ' to sign', sub: 'Awaiting your signature', color: '#1565c0', bg: '#e3f2fd', path: '/contracts' });
                if (na.change_orders_pending > 0) items.push({ label: na.change_orders_pending + ' change order' + (na.change_orders_pending > 1 ? 's' : '') + ' pending', sub: 'Needs review', color: '#6a1b9a', bg: '#f3e5f5', path: '/contracts' });
                if (na.approved_unpaid > 0) items.push({ label: na.approved_unpaid + ' approved, unpaid', sub: '$' + na.approved_unpaid_amount.toLocaleString(), color: '#2e7d32', bg: '#e8f5e9', path: '/invoices' });
                if (na.overdue_invoices > 0) items.push({ label: na.overdue_invoices + ' overdue', sub: 'Past due date', color: '#c62828', bg: '#ffebee', path: '/invoices' });
                if (items.length === 0) return null;
                return (
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: '#1a237e' }}>Needs your attention</Typography>
                    <Grid container spacing={2}>
                      {items.map((it, i) => (
                        <Grid item xs={12} sm={6} md={4} key={i}>
                          <Box onClick={() => navigate(it.path)}
                            sx={{ p: 2, borderRadius: 2, bgcolor: it.bg, cursor: 'pointer', borderLeft: '4px solid ' + it.color, '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' } }}>
                            <Typography sx={{ fontWeight: 700, color: it.color }}>{it.label}</Typography>
                            <Typography variant="caption" sx={{ color: '#666' }}>{it.sub}</Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                );
              })()}
            </>
          )}



          <ReminderWidget />
        </>
      )}


      {user?.role === 'general_contractor' && (
        <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title="Active Projects" value={projects.filter(p => p.status === 'active').length}
                icon={<FolderIcon />} color="#1a237e"
                subtitle={projects.length + ' total'} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title="Active Contracts" value={contracts.filter(c => c.status === 'active').length}
                icon={<AssignmentIcon />} color="#7b1fa2"
                subtitle={'$' + contracts.reduce((s,c) => s+Number(c.contract_amount||0),0).toLocaleString() + ' total value'} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title="Pending Invoices" value={invoices.filter(i => i.status === 'submitted').length}
                icon={<ReceiptIcon />} color="#e65100"
                subtitle={'$' + invoices.filter(i=>i.status==='submitted').reduce((s,i)=>s+Number(i.amount||0),0).toLocaleString() + ' outstanding'} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title="Total Paid Out" value={'$' + invoices.filter(i=>i.status==='paid').reduce((s,i)=>s+Number(i.amount||0),0).toLocaleString()}
                icon={<CheckCircleIcon />} color="#43a047"
                subtitle={invoices.filter(i=>i.status==='paid').length + ' invoices'} />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
            <Button variant="contained" onClick={() => navigate('/contracts')}
              sx={{ bgcolor: '#1a237e', borderRadius: 2, px: 3 }}>Contracts</Button>
            <Button variant="outlined" onClick={() => navigate('/invoices')}
              sx={{ borderColor: '#1a237e', color: '#1a237e', borderRadius: 2, px: 3 }}>Invoices</Button>
            <Button variant="outlined" onClick={() => navigate('/projects')}
              sx={{ borderColor: '#1a237e', color: '#1a237e', borderRadius: 2, px: 3 }}>Projects</Button>
            <Button variant="outlined" onClick={() => navigate('/schedule')}
              sx={{ borderColor: '#1a237e', color: '#1a237e', borderRadius: 2, px: 3 }}>Schedule</Button>
          </Box>

          <ReminderWidget />
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1a237e' }}>My Projects</Typography>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {projects.length === 0 && (
              <Grid item xs={12}>
                <Card elevation={0} sx={{ border: '1px dashed #c5cae9', borderRadius: 3, p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">No projects assigned yet.</Typography>
                </Card>
              </Grid>
            )}
            {projects.map(project => (
              <Grid item xs={12} md={6} key={project.id}>
                <Card elevation={0} onClick={() => navigate('/projects/' + project.id)}
                  sx={{ border: '1px solid #e8eaf6', borderRadius: 3, cursor: 'pointer', overflow: 'hidden',
                    '&:hover': { boxShadow: '0 4px 20px rgba(26,35,126,0.1)', transform: 'translateY(-1px)', transition: 'all 0.2s' } }}>
                  <Box sx={{ background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)', px: 3, py: 2,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, fontSize: 15 }}>{project.name}</Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>{project.address}, {project.city}</Typography>
                    </Box>
                    <Chip label={project.status} size="small"
                      sx={{ bgcolor: project.status === 'active' ? '#e8f5e9' : '#fff3e0',
                        color: project.status === 'active' ? '#2e7d32' : '#e65100', fontWeight: 700 }} />
                  </Box>
                  <CardContent sx={{ p: 2.5 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="text.secondary" display="block">Budget</Typography>
                        <Typography variant="body2" fontWeight={700} color="#1a237e">${Number(project.total_budget).toLocaleString()}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="text.secondary" display="block">Contracts</Typography>
                        <Typography variant="body2" fontWeight={700}>{contracts.filter(c => c.project_id === project.id).length}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="text.secondary" display="block">Invoices</Typography>
                        <Typography variant="body2" fontWeight={700}>{invoices.filter(i => i.project_id === project.id).length}</Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1a237e' }}>Recent Contracts</Typography>
          <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3, overflow: 'hidden', mb: 4 }}>
            {contracts.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">No contracts yet.</Typography>
              </Box>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f5f6fa' }}>
                    {['Project','Scope','Subcontractor','Amount','Status',''].map(h => (
                      <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#666' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {contracts.slice(0,5).map((c, idx) => (
                    <tr key={c.id} style={{ borderTop: '1px solid #f0f0f0', background: idx%2===0?'white':'#fafafa' }}>
                      <td style={{ padding: '10px 16px', fontSize: 13, fontWeight: 600 }}>{c.project_name||'—'}</td>
                      <td style={{ padding: '10px 16px', fontSize: 12, color: '#555' }}>{c.scope_title||'—'}</td>
                      <td style={{ padding: '10px 16px', fontSize: 12, color: '#555' }}>{c.first_name ? c.first_name+' '+c.last_name : '—'}</td>
                      <td style={{ padding: '10px 16px', fontSize: 13, fontWeight: 700, color: '#1a237e' }}>${Number(c.contract_amount).toLocaleString()}</td>
                      <td style={{ padding: '10px 16px' }}>
                        <Chip label={c.status?.replace(/_/g,' ')} size="small"
                          sx={{ bgcolor: c.status==='active'?'#e8f5e9':'#fff3e0', color: c.status==='active'?'#2e7d32':'#e65100', fontWeight: 600, fontSize: 10 }}/>
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        <Typography variant="caption" onClick={() => navigate('/contracts')}
                          sx={{ color: '#1a237e', cursor: 'pointer', fontWeight: 600 }}>View →</Typography>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1a237e' }}>Recent Invoices</Typography>
          <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3, overflow: 'hidden' }}>
            {invoices.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">No invoices yet.</Typography>
              </Box>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f5f6fa' }}>
                    {['Invoice #','Project','Amount','Status',''].map(h => (
                      <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#666' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {invoices.slice(0,5).map((inv, idx) => (
                    <tr key={inv.id} style={{ borderTop: '1px solid #f0f0f0', background: idx%2===0?'white':'#fafafa' }}>
                      <td style={{ padding: '10px 16px', fontSize: 12, color: '#555', fontFamily: 'monospace' }}>{inv.invoice_number}</td>
                      <td style={{ padding: '10px 16px', fontSize: 13, fontWeight: 600 }}>{inv.project_name||'—'}</td>
                      <td style={{ padding: '10px 16px', fontSize: 13, fontWeight: 700, color: '#1a237e' }}>${Number(inv.amount).toLocaleString()}</td>
                      <td style={{ padding: '10px 16px' }}>
                        <Chip label={inv.status} size="small"
                          sx={{ bgcolor: inv.status==='paid'?'#e8f5e9':inv.status==='submitted'?'#fff3e0':'#f5f5f5',
                            color: inv.status==='paid'?'#2e7d32':inv.status==='submitted'?'#e65100':'#757575', fontWeight: 600, fontSize: 10 }}/>
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        <Typography variant="caption" onClick={() => navigate('/invoices')}
                          sx={{ color: '#1a237e', cursor: 'pointer', fontWeight: 600 }}>View →</Typography>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </>
      )}

      {user?.role === 'subcontractor' && (() => {
        const s = subSummary;
        const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
        const agingTotal = s ? (s.aging.current + s.aging.mid + s.aging.old) : 0;
        const pct = (n) => agingTotal > 0 ? Math.round((n / agingTotal) * 100) : 0;
        const openTodos = todos.filter(t => !t.done);
        const doneTodos = todos.filter(t => t.done);
        const breakdown = s?.project_breakdown || [];
        const maxInvoiced = breakdown.reduce((mx, b) => Math.max(mx, Number(b.invoiced || 0)), 0) || 1;
        const sortedBreakdown = [...breakdown].sort((a, b) => Number(b[bdSort] || 0) - Number(a[bdSort] || 0));

        return (
          <Box>
            {/* Trial / plan-limit banner */}
            {planState?.has_subscription && (
              <>
                {!planState.can_create_contract && (
                  <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}
                    action={<Button color="inherit" size="small" onClick={() => navigate('/company-profile')}>View Plans</Button>}>
                    <strong>You've reached your {planState.plan.name} limit.</strong> {planState.create_block_reason}
                  </Alert>
                )}
                {planState.can_create_contract && planState.subscription.status === 'trial' && planState.days_until_trial_end != null && planState.days_until_trial_end <= 30 && (
                  <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}
                    action={<Button color="inherit" size="small" onClick={() => navigate('/company-profile')}>View Plans</Button>}>
                    Your free trial ends in <strong>{planState.days_until_trial_end} day{planState.days_until_trial_end === 1 ? '' : 's'}</strong>. Upgrade to keep your account active.
                  </Alert>
                )}
              </>
            )}

            {/* Greeting */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a237e', fontSize: 32 }}>
                Welcome back, {user?.first_name || 'there'}
              </Typography>
              <Typography variant="body2" color="text.secondary">Here's where things stand today.</Typography>
            </Box>

            {/* All 6 KPIs in one row, equal width */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 2, alignItems: 'stretch' }}>
              <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3, p: 2, flex: 1, minWidth: 0 }}>
                <Typography variant="caption" sx={{ color: '#999', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, fontSize: 11 }}>Contracted</Typography>
                <Typography sx={{ fontWeight: 800, color: '#1a237e', mt: 0.5, fontSize: { xs: 22, md: 22 } }}>{fmt(s?.total_contract_amount)}</Typography>
                <Typography variant="caption" sx={{ color: '#666', fontSize: 11 }}>
                  {s?.contract_count || 0} active
                </Typography>
              </Card>
              <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3, p: 2, flex: 1, minWidth: 0 }}>
                <Typography variant="caption" sx={{ color: '#999', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, fontSize: 11 }}>Invoiced</Typography>
                <Typography sx={{ fontWeight: 800, color: '#7b1fa2', mt: 0.5, fontSize: { xs: 22, md: 22 } }}>{fmt(s?.total_invoiced)}</Typography>
                <Typography variant="caption" sx={{ color: '#666', fontSize: 11 }}>
                  {s?.invoiced_count || 0} issued
                </Typography>
              </Card>
              <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3, p: 2, flex: 1, minWidth: 0 }}>
                <Typography variant="caption" sx={{ color: '#999', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, fontSize: 11 }}>Collected</Typography>
                <Typography sx={{ fontWeight: 800, color: '#2e7d32', mt: 0.5, fontSize: { xs: 22, md: 22 } }}>{fmt(s?.total_collected)}</Typography>
                <Typography variant="caption" sx={{ color: '#666', fontSize: 11 }}>
                  {s?.collected_count || 0} paid
                </Typography>
              </Card>
              <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3, p: 2, flex: 1, minWidth: 0 }}>
                <Typography variant="caption" sx={{ color: '#999', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, fontSize: 11 }}>Remaining</Typography>
                <Typography sx={{ fontWeight: 800, color: '#e65100', mt: 0.5, fontSize: { xs: 22, md: 22 } }}>{fmt(s?.total_remaining)}</Typography>
                <Typography variant="caption" sx={{ color: '#666', fontSize: 11 }}>
                  to come
                </Typography>
              </Card>
              <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3, p: 2, flex: 1, minWidth: 0, cursor: 'pointer', '&:hover': { borderColor: '#1a237e' } }}
                onClick={() => navigate('/proposals')}>
                <Typography variant="caption" sx={{ color: '#999', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, fontSize: 11 }}>Pending</Typography>
                <Typography sx={{ fontWeight: 800, color: '#1a237e', mt: 0.5, fontSize: { xs: 22, md: 22 } }}>{s?.pipeline_count || 0}</Typography>
                <Typography variant="caption" sx={{ color: '#666', fontSize: 11 }}>
                  proposal{(s?.pipeline_count || 0) === 1 ? '' : 's'}
                </Typography>
              </Card>
              <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3, p: 2, flex: 1, minWidth: 0, cursor: 'pointer', '&:hover': { borderColor: '#1a237e' } }}
                onClick={() => navigate('/scopes')}>
                <Typography variant="caption" sx={{ color: '#999', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, fontSize: 11 }}>Open Scopes</Typography>
                <Typography sx={{ fontWeight: 800, color: '#1565c0', mt: 0.5, fontSize: { xs: 22, md: 22 } }}>{s?.open_scopes_count || 0}</Typography>
                <Typography variant="caption" sx={{ color: '#666', fontSize: 11 }}>
                  to bid
                </Typography>
              </Card>
            </Box>

            {/* Aging chart full-width */}
            <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3, p: 3, mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 2, color: '#1a237e' }}>Receivables by aging</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption">Current (0–30 days)</Typography>
                    <Typography variant="caption" sx={{ color: '#666' }}>{fmt(s?.aging.current)}</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={pct(s?.aging.current || 0)} sx={{ height: 10, borderRadius: 5, bgcolor: '#f5f6fa', '& .MuiLinearProgress-bar': { bgcolor: '#2e7d32', borderRadius: 5 } }} />
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption">31–60 days</Typography>
                    <Typography variant="caption" sx={{ color: '#666' }}>{fmt(s?.aging.mid)}</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={pct(s?.aging.mid || 0)} sx={{ height: 10, borderRadius: 5, bgcolor: '#f5f6fa', '& .MuiLinearProgress-bar': { bgcolor: '#e65100', borderRadius: 5 } }} />
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption">61+ days</Typography>
                    <Typography variant="caption" sx={{ color: '#666' }}>{fmt(s?.aging.old)}</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={pct(s?.aging.old || 0)} sx={{ height: 10, borderRadius: 5, bgcolor: '#f5f6fa', '& .MuiLinearProgress-bar': { bgcolor: '#c62828', borderRadius: 5 } }} />
                </Box>
              </Box>
            </Card>

            {/* Project breakdown: chart + sortable table */}
            {breakdown.length > 0 && (
              <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3, p: 3, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a237e' }}>Billing by project</Typography>
                  <Typography variant="caption" sx={{ color: '#999' }}>{breakdown.length} project{breakdown.length === 1 ? '' : 's'}</Typography>
                </Box>

                {/* Horizontal bar chart: top projects by invoiced */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 3 }}>
                  {sortedBreakdown.slice(0, 6).map((b, i) => (
                    <Box key={'bar-' + i}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70%' }}>{b.project_address}</Typography>
                        <Typography variant="caption" sx={{ color: '#666' }}>{fmt(b.invoiced)}</Typography>
                      </Box>
                      <Box sx={{ position: 'relative', height: 14, bgcolor: '#f5f6fa', borderRadius: 5, overflow: 'hidden' }}>
                        <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${Math.max(2, Math.round((Number(b.invoiced || 0) / maxInvoiced) * 100))}%`, background: 'linear-gradient(90deg, #1a237e, #3949ab)', borderRadius: 5 }} />
                        <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${Math.max(0, Math.round((Number(b.collected || 0) / maxInvoiced) * 100))}%`, bgcolor: '#2e7d32', opacity: 0.85, borderRadius: 5 }} />
                      </Box>
                    </Box>
                  ))}
                  <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Box sx={{ width: 10, height: 10, borderRadius: 2, background: 'linear-gradient(90deg, #1a237e, #3949ab)' }} /><Typography variant="caption" sx={{ color: '#999' }}>Invoiced</Typography></Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Box sx={{ width: 10, height: 10, borderRadius: 2, bgcolor: '#2e7d32' }} /><Typography variant="caption" sx={{ color: '#999' }}>Collected</Typography></Box>
                  </Box>
                </Box>

                {/* Sortable table */}
                <Box sx={{ overflowX: 'auto' }}>
                  <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, '& th, & td': { textAlign: 'left', py: 1, px: 1, borderBottom: '1px solid #eef0f7' }, '& th': { color: '#888', fontWeight: 600, cursor: 'pointer', userSelect: 'none' }, '& td': { color: '#333' } }}>
                    <Box component="thead">
                      <Box component="tr">
                        <Box component="th">Project</Box>
                        {[['contracted','Contracted'],['invoiced','Invoiced'],['collected','Collected'],['outstanding','Outstanding'],['percent_billed','% Billed']].map(([key, label]) => (
                          <Box component="th" key={key} onClick={() => setBdSort(key)} sx={{ color: bdSort === key ? '#1a237e' : '#888' }}>
                            {label}{bdSort === key ? ' ▼' : ''}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {sortedBreakdown.map((b, i) => (
                        <Box component="tr" key={'row-' + i}>
                          <Box component="td" sx={{ fontWeight: 600, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.project_address}</Box>
                          <Box component="td">{fmt(b.contracted)}</Box>
                          <Box component="td">{fmt(b.invoiced)}</Box>
                          <Box component="td" sx={{ color: '#2e7d32' }}>{fmt(b.collected)}</Box>
                          <Box component="td" sx={{ color: b.outstanding > 0 ? '#e65100' : '#999' }}>{fmt(b.outstanding)}</Box>
                          <Box component="td">{b.percent_billed}%</Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Card>
            )}

            {/* Needs attention + My todos */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3, p: 3, height: '100%' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a237e', mb: 2 }}>⚠ Needs attention</Typography>
                  {s && (s.needs_attention.overdue > 0 || s.needs_attention.pending_signature > 0 || s.needs_attention.next_milestone) ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                      {s.needs_attention.overdue > 0 && (
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline' }}>
                          <Chip label={`${s.needs_attention.overdue} overdue`} size="small" sx={{ bgcolor: '#ffebee', color: '#c62828', fontWeight: 700, height: 22 }} />
                          <Typography variant="caption" sx={{ color: '#666' }}>invoices past 30 days</Typography>
                        </Box>
                      )}
                      {s.needs_attention.pending_signature > 0 && (
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline' }}>
                          <Chip label={`${s.needs_attention.pending_signature} contract${s.needs_attention.pending_signature === 1 ? '' : 's'}`} size="small" sx={{ bgcolor: '#fff8e1', color: '#e65100', fontWeight: 700, height: 22 }} />
                          <Typography variant="caption" sx={{ color: '#666' }}>awaiting your signature</Typography>
                        </Box>
                      )}
                      {s.needs_attention.next_milestone && (
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline' }}>
                          <Chip label={`${s.needs_attention.next_milestone.days_until} day${s.needs_attention.next_milestone.days_until === 1 ? '' : 's'}`} size="small" sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 700, height: 22 }} />
                          <Typography variant="caption" sx={{ color: '#666' }}>
                            until "{s.needs_attention.next_milestone.title}" on {s.needs_attention.next_milestone.project_address}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="caption" sx={{ color: '#999' }}>Nothing urgent — nice work.</Typography>
                  )}
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3, p: 3, height: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a237e' }}>✓ My todos</Typography>
                    <Typography variant="caption" sx={{ color: '#999' }}>
                      {openTodos.length} open{doneTodos.length ? ` • ${doneTodos.length} done` : ''}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5, maxHeight: 160, overflowY: 'auto' }}>
                    {openTodos.length === 0 && doneTodos.length === 0 && (
                      <Typography variant="caption" sx={{ color: '#999' }}>No todos yet — add one below.</Typography>
                    )}
                    {openTodos.map(t => (
                      <Box key={t.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 0.5, borderRadius: 1, '&:hover': { bgcolor: '#f5f6fa' } }}>
                        <Box onClick={() => toggleTodo(t)} sx={{ width: 16, height: 16, border: '1.5px solid #999', borderRadius: 0.5, cursor: 'pointer', flexShrink: 0 }} />
                        <Typography variant="body2" sx={{ flex: 1, fontSize: 13 }}>{t.text}</Typography>
                        <Typography onClick={() => deleteTodo(t)} variant="caption" sx={{ color: '#bbb', cursor: 'pointer', '&:hover': { color: '#c62828' } }}>×</Typography>
                      </Box>
                    ))}
                    {doneTodos.map(t => (
                      <Box key={t.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 0.5, opacity: 0.55 }}>
                        <Box onClick={() => toggleTodo(t)} sx={{ width: 16, height: 16, bgcolor: '#2e7d32', borderRadius: 0.5, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>✓</Box>
                        <Typography variant="body2" sx={{ flex: 1, fontSize: 13, textDecoration: 'line-through' }}>{t.text}</Typography>
                        <Typography onClick={() => deleteTodo(t)} variant="caption" sx={{ color: '#bbb', cursor: 'pointer', '&:hover': { color: '#c62828' } }}>×</Typography>
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField size="small" placeholder="+ add a todo" value={newTodoText}
                      onChange={e => setNewTodoText(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') addTodo(); }}
                      fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: 13 } }}
                      disabled={todoSaving} />
                    <Button onClick={addTodo} variant="text" disabled={todoSaving || !newTodoText.trim()}
                      sx={{ minWidth: 60, fontSize: 12, color: '#1a237e' }}>Add</Button>
                  </Box>
                </Card>
              </Grid>
            </Grid>

            {/* Cash flow snapshot */}
            {(() => {
              const cf = s?.cash_flow;
              if (!cf) return null;
              const tile = (label, value, sub, color, bg) => (
                <Box sx={{ flex: 1, minWidth: 150, p: 2, borderRadius: 2, bgcolor: bg, border: '1px solid #eef0f7' }}>
                  <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: 10 }}>{label}</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, color, mt: 0.5, lineHeight: 1.1 }}>{fmt(value)}</Typography>
                  {sub && <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.25 }}>{sub}</Typography>}
                </Box>
              );
              const awaitingTotal = cf.awaiting_due_later + cf.awaiting_overdue;
              const forecast = s?.cash_forecast || [];
              const forecastTotal = forecast.reduce((t, f) => t + (f.total || 0), 0);
              const forecastMax = forecast.reduce((mx, f) => Math.max(mx, f.total || 0), 0) || 1;
              return (
                <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3, p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a237e' }}>Cash flow snapshot</Typography>
                    <Typography variant="caption" sx={{ color: '#999' }}>money in · owed · expected</Typography>
                  </Box>

                  {/* Top row: money in / awaiting / expected */}
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                    {tile('Collected (30d)', cf.collected_30d, 'received last 30 days', '#2e7d32', '#f4faf0')}
                    {tile('Awaiting payment', awaitingTotal, `${fmt(cf.awaiting_overdue)} overdue`, '#e65100', '#fff8f1')}
                    {tile('Expected (6 mo)', forecastTotal, 'invoiced + projected', '#1565c0', '#f1f7fd')}
                  </Box>

                  {/* Awaiting payment split bar */}
                  <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#666', fontWeight: 600 }}>Invoiced &amp; awaiting payment</Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>{fmt(awaitingTotal)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', height: 12, borderRadius: 5, overflow: 'hidden', bgcolor: '#f5f6fa' }}>
                      {awaitingTotal > 0 && (
                        <>
                          <Box sx={{ width: `${Math.round((cf.awaiting_due_later / awaitingTotal) * 100)}%`, bgcolor: '#1976d2' }} />
                          <Box sx={{ width: `${Math.round((cf.awaiting_overdue / awaitingTotal) * 100)}%`, bgcolor: '#c62828' }} />
                        </>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, mt: 0.75 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Box sx={{ width: 10, height: 10, borderRadius: 2, bgcolor: '#1976d2' }} /><Typography variant="caption" sx={{ color: '#999' }}>Not yet due {fmt(cf.awaiting_due_later)}</Typography></Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Box sx={{ width: 10, height: 10, borderRadius: 2, bgcolor: '#c62828' }} /><Typography variant="caption" sx={{ color: '#999' }}>Overdue {fmt(cf.awaiting_overdue)}{cf.awaiting_overdue_count > 0 ? ` (${cf.awaiting_overdue_count})` : ''}</Typography></Box>
                    </Box>
                  </Box>

                  {/* 6-month cash forecast bar chart */}
                  {forecast.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Typography variant="caption" sx={{ color: '#666', fontWeight: 600 }}>Expected cash — next 6 months</Typography>
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Box sx={{ width: 10, height: 10, borderRadius: 2, bgcolor: '#1976d2' }} /><Typography variant="caption" sx={{ color: '#999' }}>Invoiced</Typography></Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Box sx={{ width: 10, height: 10, borderRadius: 2, bgcolor: '#7e57c2' }} /><Typography variant="caption" sx={{ color: '#999' }}>Projected</Typography></Box>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, height: 180, px: 1 }}>
                        {forecast.map((f, i) => {
                          const barH = Math.round((f.total / forecastMax) * 140);
                          const invH = f.total > 0 ? Math.round((f.invoiced_awaiting / f.total) * barH) : 0;
                          const projH = barH - invH;
                          return (
                            <Box key={'fc-' + i} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0 }}>
                              <Typography variant="caption" sx={{ color: f.total > 0 ? '#333' : '#ccc', fontWeight: 600, fontSize: 10, mb: 0.5, whiteSpace: 'nowrap' }}>
                                {f.total > 0 ? (f.total >= 1000 ? '$' + Math.round(f.total / 1000) + 'k' : fmt(f.total)) : '—'}
                              </Typography>
                              <Box sx={{ width: '100%', maxWidth: 48, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: 140 }}>
                                {projH > 0 && <Box sx={{ height: projH, bgcolor: '#7e57c2', borderRadius: '4px 4px 0 0' }} />}
                                {invH > 0 && <Box sx={{ height: invH, bgcolor: '#1976d2', borderRadius: projH > 0 ? 0 : '4px 4px 0 0' }} />}
                                {f.total === 0 && <Box sx={{ height: 3, bgcolor: '#eef0f7', borderRadius: 2 }} />}
                              </Box>
                              <Typography variant="caption" sx={{ color: '#999', mt: 0.75, fontSize: 11 }}>{f.label}</Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}

                  {cf.expected_upcoming_count > 0 && (
                    <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 1.5 }}>
                      {cf.expected_upcoming_count} upcoming milestone{cf.expected_upcoming_count === 1 ? '' : 's'} worth {fmt(cf.expected_upcoming_total)} not yet invoiced.
                    </Typography>
                  )}
                </Card>
              );
            })()}
          </Box>
        );
      })()}

      {/* Reminder Dialog */}
      <Dialog open={reminderDialog} onClose={() => setReminderDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#1a237e', borderBottom: '1px solid #e0e0e0', pb: 2 }}>
          📋 Add Task / Reminder
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Title *" value={reminderForm.title}
              onChange={e => setReminderForm({ ...reminderForm, title: e.target.value })}
              fullWidth placeholder="e.g. Review contract, Call supplier"/>
            <TextField label="Notes" value={reminderForm.note} multiline rows={2}
              onChange={e => setReminderForm({ ...reminderForm, note: e.target.value })}
              fullWidth placeholder="Additional details..."/>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField select label="Priority" value={reminderForm.priority}
                onChange={e => setReminderForm({ ...reminderForm, priority: e.target.value })} fullWidth>
                <MenuItem value="high">🔴 High</MenuItem>
                <MenuItem value="medium">🟡 Medium</MenuItem>
                <MenuItem value="low">🟢 Low</MenuItem>
              </TextField>
              <TextField label="Due Date" type="date" value={reminderForm.due_date}
                onChange={e => setReminderForm({ ...reminderForm, due_date: e.target.value })}
                fullWidth InputLabelProps={{ shrink: true }}/>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0', gap: 1 }}>
          <Button onClick={() => setReminderDialog(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={saveReminder} disabled={reminderSaving || !reminderForm.title}
            sx={{ bgcolor: '#1a237e', borderRadius: 2, px: 3 }}>
            {reminderSaving ? 'Saving…' : 'Add Task'}
          </Button>
        </DialogActions>
      </Dialog>

    </Layout>
  );
}
